import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Flag,
  User,
  Play,
  Star,
  Medal,
  Trophy,
  History,
  LogOut,
  ChevronRight,
  MapPin,
  Sparkles,
} from "lucide-react";
import { ProfileSetupModal } from "@/components/ProfileSetupModal";

export default function Dashboard() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [rounds, setRounds] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [publicTournaments, setPublicTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (user && !user.profile_completed) setShowProfileSetup(true);
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API}/notifications`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          data.forEach(n => toast.info(n.message, { duration: 5000 }));
          await fetch(`${API}/notifications/mark-read`, { method: "POST", credentials: "include" });
        }
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  }, []);

  const fetchRounds = useCallback(async () => {
    try {
      const [v1Res, v2Res] = await Promise.all([
        fetch(`${API}/rounds`, { credentials: "include" }),
        fetch(`${API}/v2/rounds`, { credentials: "include" }),
      ]);
      const v1 = v1Res.ok ? await v1Res.json() : [];
      const v2 = v2Res.ok ? (await v2Res.json()).map(r => ({ ...r, is_v2: true })) : [];
      setRounds([...v2, ...v1]);
    } catch {
      toast.error("Error al cargar las rondas");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/me/events`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTournaments(data.tournaments || []);
        setRankings(data.rankings || []);
      }
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchRounds();
    fetchNotifications();
    fetchMyEvents();
    fetch(`${API}/public/tournaments`)
      .then((r) => r.ok ? r.json() : { tournaments: [] })
      .then((d) => setPublicTournaments(d.tournaments || []))
      .catch(() => {});
  }, [fetchRounds, fetchNotifications, fetchMyEvents]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const activeRound = rounds.find(r => r.status === "in_progress");
  const eventsCount = tournaments.length + rankings.length;
  const recentRounds = rounds.filter(r => r.status !== "in_progress").slice(0, 3);

  const resumeRound = (round) => {
    if (round.is_v2) navigate(`/play/${round.round_id}`);
    else navigate(`/round/${round.round_id}/play`);
  };

  const openRound = (round) => {
    if (round.is_v2) {
      if (round.status === "completed") navigate(`/play/${round.round_id}/results`);
      else navigate(`/play/${round.round_id}`);
      return;
    }
    if (round.status === "setup") navigate(`/round/${round.round_id}/players`);
    else if (round.status === "in_progress") navigate(`/round/${round.round_id}/play`);
    else navigate(`/round/${round.round_id}`);
  };

  const statusBadge = (status) => {
    const map = {
      setup: ["Configurando", "bg-yellow-100 text-yellow-700"],
      in_progress: ["En juego", "bg-blue-100 text-blue-700"],
      completed: ["Completada", "bg-green-100 text-green-700"],
    };
    const [txt, cls] = map[status] || ["", "hidden"];
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{txt}</span>;
  };

  return (
    <div className="min-h-screen bg-[#F7F3EA]" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/cadii/brand/logo_c_circle.png" alt="" className="w-8 h-8 object-contain" />
            <span className="text-xl font-black tracking-[0.18em] text-[#102E32]">CADii</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <User className="w-5 h-5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-[#64748B]">{user?.email}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-menu-btn">
                <User className="w-4 h-4 mr-2 text-[#103B31]" /> Mi Perfil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/rounds")} data-testid="rounds-history-menu-btn">
                <History className="w-4 h-4 mr-2 text-[#103B31]" /> Historial de Rondas
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/players/favorites")} data-testid="favorites-menu-btn">
                <Star className="w-4 h-4 mr-2 text-amber-500" /> Jugadores Favoritos
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-10 pb-16 flex flex-col min-h-[calc(100vh-65px)]">
        {/* Greeting */}
        <div className="mb-8 flex items-center gap-3">
          <img src="/cadii/mascots/pencil_golfer_thumbsup.png" alt="" className="w-14 h-14 object-contain flex-shrink-0" />
          <div>
            <h2 className="text-3xl sm:text-4xl font-black text-[#102E32] tracking-tight" data-testid="welcome-heading">
              ¡Hola, {user?.name?.split(" ")[0]}!
            </h2>
            <p className="text-sm text-[#64748B] mt-0.5">¿Listo para mejorar tu juego?</p>
          </div>
        </div>

        {/* Active round banner */}
        {!loading && activeRound && (
          <button
            onClick={() => resumeRound(activeRound)}
            className="w-full mb-6 p-5 rounded-2xl bg-[#103B31] text-white text-left hover:bg-[#092B25] transition-all active:scale-[0.98] animate-fade-in"
            style={{ boxShadow: "0 8px 24px rgba(16,46,50,.14)" }}
            data-testid="resume-round-banner"
          >
            <p className="text-[10px] uppercase tracking-widest text-[#E6B733] font-bold mb-1">Ronda en curso</p>
            <p className="text-lg font-bold truncate mb-3">{activeRound.name}</p>
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E8DFC8] text-[#102E32] text-xs font-black uppercase tracking-wider">
              <Play className="w-3.5 h-3.5" fill="currentColor" /> Continuar Ronda
            </span>
          </button>
        )}

        {/* Main action: Create round */}
        <button
          onClick={() => navigate("/create-round")}
          className="w-full h-44 rounded-3xl bg-[#103B31] text-white flex flex-col items-center justify-center gap-3 hover:bg-[#092B25] transition-all active:scale-[0.98] mb-4"
          style={{ boxShadow: "0 8px 24px rgba(16,46,50,.14)" }}
          data-testid="start-round-btn"
        >
          <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center p-3">
            <img src="/cadii/golf-icons/golf_ball.png" alt="" className="w-full h-full object-contain" />
          </div>
          <span className="text-2xl font-black tracking-tight">Crear Ronda</span>
          <span className="text-xs text-white/60">Tipo de juego · jugadores · apuestas</span>
        </button>

        {/* Social Studio entry */}
        <button
          onClick={() => navigate("/studio")}
          className="w-full flex items-center gap-3 rounded-2xl bg-white border-2 border-gray-200 p-4 mb-4 hover:border-[#103B31]/30 transition-all active:scale-[0.98]"
          data-testid="studio-btn"
        >
          <div className="w-11 h-11 rounded-full bg-[#103B31]/8 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[#103B31]" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-base font-black text-[#102E32]">Crear Contenido</p>
            <p className="text-xs text-gray-400">Imágenes y videos para compartir</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300" />
        </button>

        {/* Tournaments */}
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <button
              className="w-full h-44 rounded-3xl bg-white border-2 border-gray-200 text-[#0F172A] flex flex-col items-center justify-center gap-3 hover:border-gray-300 transition-all active:scale-[0.98]"
              data-testid="menu-btn"
            >
              <div className="w-16 h-16 rounded-full bg-[#E6B733]/15 flex items-center justify-center relative p-3">
                <img src="/cadii/golf-icons/trophy_c.png" alt="" className="w-full h-full object-contain" />
                {eventsCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center" data-testid="events-badge">
                    {eventsCount}
                  </span>
                )}
              </div>
              <span className="text-2xl font-black tracking-tight">Ver Torneos</span>
              <span className="text-xs text-gray-400">Mis torneos y rankings</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto" style={{ fontFamily: "Manrope, sans-serif" }}>
            <SheetHeader className="mb-4">
              <SheetTitle className="flex items-center gap-2 text-left">
                <Trophy className="w-5 h-5 text-amber-600" /> Mis Torneos
              </SheetTitle>
              <SheetDescription className="text-left text-xs">
                Torneos y rankings donde estás inscrito
              </SheetDescription>
            </SheetHeader>

            {eventsCount === 0 && (
              <div className="text-center py-10" data-testid="no-events-empty-state">
                <img src="/cadii/mascots/golfball_putting.png" alt="" className="w-20 h-20 object-contain mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">No estás inscrito en ningún torneo</p>
                <p className="text-xs text-gray-400 mt-1">Cuando tu club te inscriba, aparecerán aquí</p>
              </div>
            )}

            {tournaments.length > 0 && (
              <div className="mb-5" data-testid="menu-tournaments-section">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2 px-1">Mis Torneos</p>
                <div className="space-y-1.5">
                  {tournaments.map(t => (
                    <button key={t.tournament_id}
                      onClick={() => { setMenuOpen(false); t.club?.slug && navigate(`/${t.club.slug}`); }}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-left transition-colors"
                      data-testid={`menu-tournament-${t.tournament_id}`}>
                      <Medal className="w-5 h-5 text-[#103B31] flex-shrink-0" strokeWidth={1.5} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#0F172A] truncate">{t.name}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.club?.name}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {rankings.length > 0 && (
              <div className="mb-5" data-testid="menu-rankings-section">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2 px-1">Mis Rankings</p>
                <div className="space-y-1.5">
                  {rankings.map(r => (
                    <button key={r.ranking_id}
                      onClick={() => { setMenuOpen(false); r.club?.slug && navigate(`/${r.club.slug}/rankings/${r.ranking_id}`); }}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-left transition-colors"
                      data-testid={`menu-ranking-${r.ranking_id}`}>
                      <Trophy className="w-5 h-5 text-amber-600 flex-shrink-0" strokeWidth={1.5} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#0F172A] truncate">{r.name}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.club?.name}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300" />
                    </button>
                  ))}
                </div>
              </div>
            )}

          </SheetContent>
        </Sheet>

        {/* Recent rounds */}
        {!loading && (
          <div className="mt-8" data-testid="recent-rounds-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <img src="/cadii/mascots/scorecard_walk.png" alt="" className="w-9 h-9 object-contain" />
                <h3 className="font-black text-[#0F172A] tracking-tight">Historial de Rondas</h3>
              </div>
              {rounds.length > 0 && (
                <button onClick={() => navigate("/rounds")}
                  className="flex items-center gap-0.5 text-xs font-bold text-[#103B31] hover:text-[#092B25]"
                  data-testid="see-all-rounds-btn">
                  Ver todas <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {rounds.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center" data-testid="no-rounds-empty-state">
                <img src="/cadii/mascots/golfball_putting.png" alt="" className="w-16 h-16 object-contain mx-auto mb-2" />
                <p className="text-sm text-gray-500 font-medium">Aún no tienes rondas</p>
                <p className="text-xs text-gray-400">Tu historial aparecerá aquí</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentRounds.map(r => (
                  <button key={r.round_id} onClick={() => openRound(r)}
                    className="w-full flex items-center gap-3 bg-white rounded-2xl border border-gray-100 p-3.5 text-left hover:shadow-md transition-shadow"
                    data-testid={`recent-round-${r.round_id}`}>
                    <div className="w-9 h-9 rounded-xl bg-[#103B31]/5 flex items-center justify-center flex-shrink-0">
                      <Flag className="w-4 h-4 text-[#103B31]" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-[#0F172A] truncate">{r.name}</p>
                        {statusBadge(r.status)}
                      </div>
                      <p className="text-xs text-gray-400 truncate">
                        {r.date}{(typeof r.course === "object" ? r.course?.course_name : r.course) ? ` · ${typeof r.course === "object" ? r.course?.course_name : r.course}` : ""}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-3 border-[#103B31] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Public competitions discovery feed */}
        {publicTournaments.length > 0 && (
          <div className="mt-8" data-testid="public-competitions-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Flag className="w-4 h-4 text-emerald-600" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-black text-[#0F172A] tracking-tight">Competencias públicas</h3>
                <p className="text-xs text-gray-400">Torneos abiertos de clubes en cadii</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {publicTournaments.map((t) => (
                <button
                  key={t.tournament_id}
                  onClick={() => navigate(`/${t.club?.slug}/tournaments/${t.tournament_id}`)}
                  className="w-full flex items-center gap-3 bg-white rounded-2xl border border-gray-100 p-3.5 text-left hover:shadow-md transition-shadow"
                  data-testid={`public-tournament-${t.tournament_id}`}
                >
                  {t.club?.logo_url ? (
                    <img src={t.club.logo_url} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0 bg-gray-50" />
                  ) : (
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-[#0F172A] truncate">{t.name}</p>
                      {t.status === "in_progress" && <span className="text-[10px] font-bold text-red-600">● EN VIVO</span>}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{t.club?.name}{t.course_name ? ` · ${t.course_name}` : ""}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {showProfileSetup && (
        <ProfileSetupModal
          user={user}
          onComplete={(updated) => {
            setUser(prev => ({ ...prev, ...updated, profile_completed: true }));
            setShowProfileSetup(false);
          }}
          onSkip={() => {
            setUser(prev => ({ ...prev, profile_completed: true }));
            setShowProfileSetup(false);
          }}
        />
      )}
    </div>
  );
}
